<template>
  <div>
    <div v-if="isLoading" class="d-flex justify-content-center mb-3 pt-4">
      <b-spinner style="width: 5rem; height: 5rem" variant="warning" label="Spinning"></b-spinner>
    </div>
    <div v-else>
      <div class="ml-3 mr-3" rounded="lg" id="overlay">
        <div class="row no-gutters mb-2 mt-4">
          <div class="col">
            <h1 class="mb-0 pb-0" data-test-hook="pageTitle">
              <i class="far fa-badge-dollar pr-1" data-test-hook="titleIcon" />
              <span data-test-hook="subscription-plan-details">{{ $t('xManageYourPlan') }}</span>
            </h1>
          </div>
        </div>
        <div v-if="showPremiumFeaturesGettingReady === true || subscriptionCancelInitiated === true">
          <b-row class="pl-1">
            <b-col cols="12" class="pt-3">
              <b-alert show class="alert alert-info">
                <b-row class="mb-2"
                  ><i class="far fa-circle-exclamation ml-2" />&nbsp;&nbsp;{{
                    $t('xProcessingSubscriptionInformation')
                  }}
                </b-row>
                <span class="ml-3" style="font-weight: normal !important">{{ $t('xProcessingPlanChange') }}</span>
              </b-alert>
            </b-col>
          </b-row>
        </div>

        <AccountNeedsAttentionBanner
          v-if="
            numberOfApparatusInNeedOfAttention > 0 ||
              (apparatusMultiplePlans !== null && premiumSubscriptionApparatusMultiplePlans)
          "
          :productsOnBasicSubscription="productsOnBasicSubscription"
          :apparatusesNeedsAttention="premiumApparatusesNeedsAttention"
          :apparatusHasMultipleSubscriptions="apparatusMultiplePlans"
          :premiumSubscriptionApparatusMultiplePlans="premiumSubscriptionApparatusMultiplePlans"
          :allValidApparatusesOnAccount="apparatuses"
          :currentSubscription="
            subsForPremiumPlanCard?.filter(
              sub => sub.subscriptionStatus !== subscriptionDetailCanceled && sub.subscriptionStore !== dealerStore
            )[0]
          "
          @openCancelPlanMultiplePlansModal="openCancelPlanMultiplePlansModal"
          @openRemoveApparatusMultiplePlansModal="openRemoveApparatusMultiplePlansModal"
          :disableUpgradeButton="showPremiumFeaturesGettingReady || subscriptionCancelInitiated === true"
        />
        <NoProductsOnAccount v-if="!doesAccountHaveAnyProducts()" />

        <div v-for="(productDealerManaged, index) in productsDealerManaged" :key="index">
          <DealerManaged :productDealerManaged="productDealerManaged" />
        </div>

        <LegacySubscriptionList
          v-if="legacySubscriptionList?.length > 0"
          :subscriptionList="legacySubscriptionList !== undefined ? legacySubscriptionList : []"
          @openCancelSubscriptionModal="openCancelSubscriptionModal"
        />
        <PremiumPlan
          v-if="this.subsForPremiumPlanCard?.length > 0"
          :subscription="
            subsForPremiumPlanCard?.filter(sub => sub.subscriptionStatus !== subscriptionDetailCanceled)[0]
          "
          :apparatusesNeedsAttention="premiumApparatusesNeedsAttention"
          :premiumSubscriptionApparatusMultiplePlans="premiumSubscriptionApparatusMultiplePlans"
          @openCancelPlanMultiplePlansModal="openCancelPlanMultiplePlansModal"
        />

        <HasProductNoSubscription
          v-if="productsOnBasicSubscription?.length > 0"
          :productsOnBasicSubscription="productsOnBasicSubscription"
          :apparatusesOnExistingSubscription="apparatusesOnExistingSubscription"
          :legacySubscriptionList="legacySubscriptionList"
          :accountProfile="accountProfile"
          :showBasicSubscriptionCard="showBasicSubscriptionCard"
          :disableUpgradeButton="showPremiumFeaturesGettingReady"
        />

        <div v-for="(v1FuelMonitor, index) in v1FuelMonitors" :key="index">
          <HasV1FuelMonitor :v1FuelMonitor="v1FuelMonitor" />
        </div>

        <div class="row mt-2 mb-2" />
        <PaymentInfo v-if="existingPaymentInfo && purchasedOnWeb" />

        <CancelSubscriptionModal
          :isCancelLegacy="isCancelLegacy"
          :endDate="endDate"
          :cancelLegacyApparatusId="cancelLegacyApparatusId !== undefined ? cancelLegacyApparatusId : null"
          :cancelLegacyApparatusType="cancelLegacyApparatusType !== undefined ? cancelLegacyApparatusType : null"
          :cancelSubscriptionApparatuses="cancelSubscriptionApparatuses !== null ? cancelSubscriptionApparatuses : []"
          :cancelSubscriptionProductId="cancelSubscriptionProductId !== undefined ? cancelSubscriptionProductId : null"
          :cancelSubscriptionStore="cancelSubscriptionStore !== undefined ? cancelSubscriptionStore : null"
        />
        <CancelPremiumPlanApparatusMultiplePlansModal
          :accountProfile="accountProfile"
          :apparatus="apparatusMultiplePlans"
          :currentPremiumSubscription="premiumSubscriptionApparatusMultiplePlans"
          :immediatelyOpen="this.apparatusMultipleSubs !== undefined && this.apparatusMultipleSubs !== null"
        />
        <AccountNeedsAttentionModal />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { validationMixin } from 'vuelidate'
import * as Constants from '@/constants'

import { displayShortDate, subscriptionDetailActive, subscriptionManaged } from '../../../utils/mixins'

import AccountNeedsAttentionBanner from './AccountNeedsAttentionBanner.vue'
import AccountNeedsAttentionModal from './AccountNeedsAttentionModal.vue'
import CancelPremiumPlanApparatusMultiplePlansModal from './CancelPremiumPlanApparatusMultiplePlansModal.vue'
import CancelSubscriptionModal from './CancelSubscriptionModal.vue'
import DealerManaged from './DealerManaged.vue'
import HasProductNoSubscription from './HasProductNoSubscription.vue'
import NoProductsOnAccount from './NoProductsOnAccount.vue'
import LegacySubscriptionList from './LegacySubscriptionList.vue'
import PaymentInfo from './PaymentInfo.vue'
import PremiumPlan from './PremiumPlan.vue'
import HasV1FuelMonitor from './HasV1FuelMonitor.vue'

export default {
  components: {
    AccountNeedsAttentionBanner,
    AccountNeedsAttentionModal,
    CancelPremiumPlanApparatusMultiplePlansModal,
    CancelSubscriptionModal,
    DealerManaged,
    HasProductNoSubscription,
    LegacySubscriptionList,
    NoProductsOnAccount,
    PaymentInfo,
    PremiumPlan,
    PremiumPlan,
    HasV1FuelMonitor
  },
  props: ['apparatusMultipleSubs'],
  mixins: [validationMixin],
  data() {
    return {
      apparatuses: [],
      cancelLegacyApparatusId: null,
      cancelLegacyApparatusType: null,
      cancelSubscriptionProductId: null,
      cancelSubscriptionStore: null,
      cancelSubscriptionApparatuses: [],
      endDate: null,
      existingPaymentInfo: null,
      numberOfApparatusInNeedOfAttention: 0,
      isCancelLegacy: false,
      isLoading: true,
      legacySubscriptionList: [],
      premiumApparatusesNeedsAttention: [],
      productsDealerManaged: [],
      v1FuelMonitors: [],
      productsOnBasicSubscription: [],
      apparatusesOnExistingSubscription: [],
      purchasedOnWeb: true,
      subscriptions: [],
      subscriptionDetails: null,
      showPremiumFeaturesGettingReady: false,
      apparatusMultiplePlans: null,
      premiumSubscriptionApparatusMultiplePlans: null,
      subsForPremiumPlanCard: []
    }
  },
  computed: {
    ...mapState({
      apparatusesFromState: state => state.apparatus.apparatuses,
      accountProfile: state => state.account.accountProfile,
      apparatusSubscriptionsGettingReady: state => state.subscriptions.apparatusSubscriptionsGettingReady,
      subscriptionCancelInitiated: state => state.subscriptions.subscriptionCancelInitiated
    }),
    dealerManagedProductId() {
      return Constants.DealerManagedProductId
    },
    subscriptionDetailCanceled() {
      return Constants.SubscriptionDetailCanceled
    },
    tankUtilityDealerManagedProductId() {
      return Constants.TankUtilityDealerManagedProductId
    },
    webStore() {
      return Object.values(Constants.SubscriptionStoreTypes).indexOf('Web')
    },
    appleStore() {
      return Object.values(Constants.SubscriptionStoreTypes).indexOf('Apple')
    },
    googleStore() {
      return Object.values(Constants.SubscriptionStoreTypes).indexOf('Google')
    },
    dealerStore() {
      return Object.values(Constants.SubscriptionStoreTypes).indexOf('Dealer')
    },
    showBasicSubscriptionCard() {
      return (
        this.productsOnBasicSubscription?.length > 0 &&
        this.subsForPremiumPlanCard?.length === 0 &&
        !this.legacySubscriptionList?.some(
          legacySub => legacySub.subscriptionStatus !== Constants.SubscriptionCancelled
        )
      )
    }
  },
  methods: {
    async calcShowPremiumFeaturesGettingReady() {
      //need to do this to get the apparatusSubscriptionsGettingReady data within a child component
      var apparatusSubscriptionsGettingReady = await this.$store.dispatch('getPremiumFeaturesGettingReadyInfo')
      this.showPremiumFeaturesGettingReady = false
      if (
        this.apparatuses === null ||
        this.apparatusSubscriptionsGettingReady === null ||
        this.apparatuses?.length === 0 ||
        this.apparatusSubscriptionsGettingReady?.length === 0
      ) {
        return
      }
      // show banner if subscription is not present but we have apparatuses and apparatusSubscriptionsGettingReady
      if (
        this.apparatuses?.length > 0 &&
        this.apparatusSubscriptionsGettingReady?.length > 0 &&
        this.subscriptions?.filter(
          subscription =>
            subscriptionDetailActive(subscription.subscriptionStatus) && !subscriptionManaged(subscription)
        ).length === 0
      ) {
        this.showPremiumFeaturesGettingReady = true
        return
      }

      // loop through each apparatus checking if it has a pending subscription change in state
      this.apparatuses.forEach(apparatus => {
        var apparatusSubscriptionInfoFromState = apparatusSubscriptionsGettingReady?.find(
          apparatusSubscription => apparatusSubscription.apparatusId === apparatus.apparatusId
        )
        // check if the apparatus has a pending subscription change(data from state),
        if (apparatusSubscriptionInfoFromState !== undefined && apparatusSubscriptionInfoFromState !== null) {
          var subscriptionDetails = this.subscriptions?.find(
            subscription =>
              subscriptionDetailActive(subscription.subscriptionStatus) &&
              subscription.apparatuses?.find(subApparatus => subApparatus.id === apparatus.apparatusId)
          )
          // check if processing has been completed by platform
          if (
            subscriptionDetails &&
            (apparatusSubscriptionInfoFromState.productSubscribedTo === subscriptionDetails?.subscriptionProductId ||
              apparatusSubscriptionInfoFromState.productSubscribedTo === subscriptionDetails?.pendingProductId)
          ) {
            this.$store.dispatch('deletePremiumFeaturesGettingReadyInfo', apparatus.apparatusId)
          } else {
            this.showPremiumFeaturesGettingReady = true
            return //found one, no need to keep looking
          }
        }
      })
      return
    },
    getSubForPremiumPlanCard() {
      this.subsForPremiumPlanCard = this.subscriptions.filter(
        subscription =>
          subscription.subscriptionStatus !== Constants.SubscriptionDetailCanceled &&
          !this.isDealerManaged(subscription)
      )
    },
    isDealerManaged(sub) {
      return (
        sub.subscriptionProductId === Constants.DealerManagedProductId ||
        sub.subscriptionProductId === Constants.TankUtilityDealerManagedProductId
      )
    },
    isAnyActiveSubscriptions() {
      return this.subscriptions.some(sub => sub.subscriptionStatus !== Constants.SubscriptionDetailCanceled)
    },
    openCancelSubscriptionModal(request) {
      this.isCancelLegacy = request.isCancelLegacy
      this.endDate = request.endDate
      this.cancelLegacyApparatusId = request.cancelLegacyApparatusId
      this.cancelLegacyApparatusType = request.cancelLegacyApparatusType
      this.openModal('cancel-subscription-modal')
    },
    openCancelPlanMultiplePlansModal() {
      switch (this.premiumSubscriptionApparatusMultiplePlans?.subscriptionStore) {
        case Constants.AppleSubscriptionStore:
        case Constants.GoogleSubscriptionStore:
          this.openModal('apparatus-with-multiple-subscriptions-mobile-platform')
          break
        case Constants.WebSubscriptionStore:
          this.openModal('cancel-single-apparatus-multiple-subscriptions')
          break
        default:
          break
      }
    },
    openRemoveApparatusMultiplePlansModal() {
      switch (this.premiumSubscriptionApparatusMultiplePlans?.subscriptionStore) {
        case Constants.AppleSubscriptionStore:
        case Constants.GoogleSubscriptionStore:
          this.openModal('remove-apparatus-with-multiple-subscriptions-mobile-platform')
          break
        case Constants.WebSubscriptionStore:
          this.openModal('remove-home-premium-apparatus-multiple-subscriptions')
          break
        default:
          break
      }
    },
    doesAccountHaveSubscription() {
      if (!this.subscriptions) return false
      return this.subscriptions.some(sub => sub.status !== Constants.SubscriptionDetailCanceled)
    },
    isApparatusBasic(apparatus) {
      if (
        apparatus.isDealerManaged ||
        this.isApparatusV1FuelTank(apparatus) ||
        this.hasActiveLegacySubscription(apparatus?.apparatusId) ||
        this.hasActiveSubscription(apparatus?.apparatusId)
      ) {
        return false
      }
      var dealerManaged = this.subscriptions?.find(x =>
        x.apparatuses?.find(y => y.apparatusId == apparatus?.apparatusId && y.isDealerManaged)
      )
      // This should never be true since DM subs are considered active subscriptions
      if (dealerManaged) {
        return false
      }
      return true
    },
    isApparatusV1FuelTank(apparatus) {
      const deviceType = apparatus.properties?.find(x => x.type === Constants.ApparatusPropertyDevice)?.value
        ?.deviceType
      if (!deviceType) return false
      if (deviceType === Constants.TankUtilityWifiDeviceType || deviceType === Constants.TankUtilityLteDeviceTypeV1)
        return true
      return false
    },
    doesAccountHaveApparatusNotOnAnyPlan() {
      const t = this.apparatuses?.find(
        x =>
          !this.hasActiveSubscription(x.apparatusId) &&
          !this.hasActiveLegacySubscription(x.apparatusId)(
            x.type === Constants.HsbApparatusType ||
              (x.type === Constants.FuelTankApparatusType &&
                x.properties?.find(x => x.type === Constants.ApparatusPropertyDevice)?.value?.deviceType ===
                  Constants.TankUtilityLteDeviceTypeV2)
          )
      )
      return t ? true : false
    },
    addMonths(purchasedDate, subscriptionDuration) {
      const shortDate = displayShortDate(purchasedDate)
      const dateParts = shortDate.split('/')
      let month = parseInt(dateParts[0])
      let day = parseInt(dateParts[1])
      let year = parseInt(dateParts[2])
      let subscriptionDurationInt = Number(subscriptionDuration)

      month = month + subscriptionDurationInt
      if (month > 12) {
        month -= 12
        year += 1
      }
      const monthString = String(month).padStart(2, '0')
      const dayString = String(day).padStart(2, '0')
      const yearString = String(year)

      return `${monthString}/${dayString}/${yearString}.`
    },
    async getPaymentMethod() {
      await this.$store
        .dispatch('getActivePaymentMethod', this.accountProfile.organizationId)
        .then(response => {
          this.existingPaymentInfo = response.data ? response.data : null
          // this.billingInfoLoading = false
        })
        .catch(error => {
          this.existingPaymentInfo = null
          // this.billingInfoLoading = false
        })
    },
    doesAccountHaveAnyProducts() {
      return this.apparatuses?.length > 0
    },
    doesApparatusHaveSubscription(apparatusId) {
      return this.subscriptions.some(subscription =>
        subscription.apparatuses.some(
          apparatus =>
            apparatus.id === apparatusId && subscription.subscriptionStatus !== Constants.SubscriptionDetailCanceled
        )
      )
    },
    getNumberOfApparatusInNeedOfAttention() {
      // NOT NEED ATTENTION
      // 1-2 gens, on or off plan

      // NEEDS ATTENTION
      // 1-2 gens, on or off plan with 1-4 FM not on plan
      // 0 gens with 1-4 FM not on plan
      // 1 hsb on plan with 1 FM that didn't finish enrollment or was removed from DM
      // 1 hsb on plan with 1 hsb removed from DM or not on a plan

      const apparatusesWithActiveLegacySubs = this.apparatuses
        ?.filter(apparatus => {
          return this.legacySubscriptionList?.some(
            legacySub =>
              legacySub.apparatusId === apparatus.apparatusId &&
              legacySub.subscriptionStatus === Constants.SubscriptionActive
          )
        })
        .map(apparatus => apparatus.apparatusId)

      const apparatusesOnActiveNonDealerSubscriptions = this.apparatuses
        ?.filter(apparatus => {
          return this.subscriptions?.some(
            subscription =>
              subscription.apparatuses.some(subApparatus => subApparatus.id === apparatus.apparatusId) &&
              subscription.subscriptionStore !== Constants.DealerSubscriptionStore &&
              subscription.subscriptionStatus !== Constants.SubscriptionDetailCanceled
          )
        })
        .map(apparatus => apparatus.apparatusId)

      const apparatusesOnActiveDealerSubscriptions = this.apparatuses
        ?.filter(apparatus => {
          return this.subscriptions?.some(
            subscription =>
              subscription.apparatuses.some(subApparatus => subApparatus.id === apparatus.apparatusId) &&
              subscription.subscriptionStore === Constants.DealerSubscriptionStore &&
              subscription.subscriptionStatus !== Constants.SubscriptionDetailCanceled
          )
        })
        .map(apparatus => apparatus.apparatusId)

      const hsbsOnANonDealerPlan = this.apparatuses?.filter(
        apparatus =>
          apparatus.type === Constants.HsbApparatusType &&
          (apparatusesOnActiveNonDealerSubscriptions.includes(apparatus.apparatusId) ||
            apparatusesWithActiveLegacySubs.includes(apparatus.apparatusId))
      )

      const hsbNotOnAPlan = this.apparatuses?.filter(
        apparatus =>
          apparatus.type === Constants.HsbApparatusType &&
          !apparatusesOnActiveNonDealerSubscriptions.includes(apparatus.apparatusId) &&
          !apparatusesWithActiveLegacySubs.includes(apparatus.apparatusId) &&
          !apparatusesOnActiveDealerSubscriptions.includes(apparatus.apparatusId)
      )

      const hsbOnCanceledDM = this.apparatuses?.filter(
        apparatus =>
          apparatus.type === Constants.HsbApparatusType &&
          this.subscriptions?.some(
            subscription =>
              subscription.apparatuses.some(subApparatus => subApparatus.id === apparatus.apparatusId) &&
              subscription.subscriptionProductId === Constants.DealerManagedProductId &&
              subscription.subscriptionStatus === Constants.SubscriptionDetailCanceled
          )
      )

      const fmsOnANonDealerPlan = this.apparatuses?.filter(
        apparatus =>
          apparatus.type === Constants.FuelTankApparatusType &&
          !this.isApparatusV1FuelTank(apparatus) &&
          (apparatusesOnActiveNonDealerSubscriptions.includes(apparatus.apparatusId) ||
            apparatusesWithActiveLegacySubs.includes(apparatus.apparatusId))
      )

      const fmsNotOnPlan = this.apparatuses?.filter(
        apparatus =>
          apparatus.type === Constants.FuelTankApparatusType &&
          !this.isApparatusV1FuelTank(apparatus) &&
          !apparatus.isDealerManaged &&
          !apparatusesOnActiveNonDealerSubscriptions.includes(apparatus.apparatusId) &&
          !apparatusesWithActiveLegacySubs.includes(apparatus.apparatusId)
      )

      const fmOnCanceledDM = this.apparatuses?.filter(
        apparatus =>
          apparatus.type === Constants.FuelTankApparatusType &&
          !this.isApparatusV1FuelTank(apparatus) &&
          !apparatus.isDealerManaged &&
          this.subscriptions?.some(
            subscription =>
              subscription.apparatuses.some(subApparatus => subApparatus.id === apparatus.apparatusId) &&
              subscription.subscriptionProductId === Constants.DealerManagedProductId &&
              subscription.subscriptionStatus === Constants.SubscriptionDetailCanceled
          )
      )

      // NOT NEED ATTENTION
      // 1-2 gens, on or off plan
      var response = 0

      // NEEDS ATTENTION
      // 1-2 gens, on or off plan with 1-4 FM not on plan
      // 0 gens with 1-4 FM not on plan
      // 1 hsb on plan with 1 FM that didn't finish enrollment or was removed from DM
      if (fmsNotOnPlan?.length > 0 || fmOnCanceledDM?.length > 0) {
        response = fmsNotOnPlan?.length + fmOnCanceledDM?.length
      }

      // apparatus(es) on plan with hsb(s) removed from DM or not on a plan
      if (
        (hsbsOnANonDealerPlan?.length > 0 || fmsOnANonDealerPlan?.length > 0) &&
        (hsbOnCanceledDM?.length > 0 || hsbNotOnAPlan?.length > 0)
      ) {
        response += hsbOnCanceledDM?.length + hsbNotOnAPlan?.length
      }

      return response
    },
    getPremiumApparatusesNeedsAttention() {
      if (
        this.subscriptions?.some(x => x.subscriptionStore !== this.webStore && x.subscriptionStore !== this.dealerStore)
      )
        return []
      let notInSubscriptionsList = this.apparatuses.filter(
        apparatus =>
          !this.doesApparatusHaveSubscription(apparatus.apparatusId) &&
          !this.isApparatusV1FuelTank(apparatus) &&
          Constants.CountriesGetPaidSubs.includes(apparatus.country.toUpperCase())
      )

      if (
        !this.subscriptions?.some(
          x =>
            x.subscriptionStatus === Constants.SubscriptionDetailActive &&
            x.subscriptionProductId !== Constants.DealerManagedProductId
        ) &&
        notInSubscriptionsList.every(apparatus => apparatus.type === Constants.HsbApparatusType) &&
        notInSubscriptionsList.length <= 2 &&
        this.legacySubscriptionList?.length < 1
      ) {
        return []
      }

      if (
        this.subscriptions?.some(x => x.subscriptionStatus === Constants.SubscriptionDetailActive) &&
        notInSubscriptionsList.length > 0
      ) {
        return (
          notInSubscriptionsList?.map(apparatus => ({
            apparatusId: apparatus.apparatusId,
            type: apparatus.type,
            apparatusName: apparatus.name
          })) ?? []
        )
      }

      //make sure appartus(es) that are not in subscriptions list, are also not in legacy subscription list
      if (this.legacySubscriptionList?.length > 0) {
        notInSubscriptionsList = notInSubscriptionsList?.filter(
          x => !this.legacySubscriptionList?.find(y => y.apparatusId == x.apparatusId)
        )
      }

      return (
        notInSubscriptionsList?.map(apparatus => ({
          apparatusId: apparatus.apparatusId,
          type: apparatus.type,
          apparatusName: apparatus.name
        })) ?? []
      )
    },
    hasActiveSubscription(apparatusId) {
      return this.subscriptions?.some(x => x.apparatuses?.find(y => y.id == apparatusId))
    },
    hasActiveLegacySubscription(apparatusId) {
      return this.legacySubscriptionList?.some(x => x?.apparatusId == apparatusId)
    },
    filterApparatusesFromState() {
      // don't include apparatuses that aren't fully enrolled (don't have a device) & for now don't include v1 tanks
      this.apparatuses = this.apparatusesFromState?.filter(apparatus =>
        apparatus.properties?.some(property => property.type === Constants.ApparatusPropertyDevice)
      )
    },
    apparatusWithMultipleSubscriptions() {
      this.apparatuses.forEach(apparatus => {
        if (
          this.subscriptions.filter(
            subscription =>
              subscription.apparatuses.some(
                subscriptionApparatus => subscriptionApparatus.id === apparatus.apparatusId
              ) && subscription.subscriptionStatus === Constants.SubscriptionDetailActive
          ).length > 1
        ) {
          this.apparatusMultiplePlans = apparatus
          this.premiumSubscriptionApparatusMultiplePlans = this.subscriptions.find(
            subscription =>
              subscription.apparatuses.some(
                subscriptionApparatus => subscriptionApparatus.id === apparatus.apparatusId
              ) && subscription.subscriptionStore !== Constants.DealerSubscriptionStore
          )
          return
        }
      })
    },
    calcProductsOnBasicSubscription() {
      this.productsOnBasicSubscription = this.apparatuses
        ?.filter(x => this.isApparatusBasic(x) === true)
        .map(apparatus => {
          return {
            apparatusId: apparatus.apparatusId,
            apparatusName: apparatus.name,
            apparatusType: apparatus.type,
            country: apparatus.country
          }
        })
    },
    isNonDealerPremiumProduct(app) {
      return this.subscriptions.find(
        subscription =>
          subscription.subscriptionStore !== Constants.DealerSubscriptionStore &&
          subscription.subscriptionStatus !== Constants.SubscriptionDetailCanceled &&
          subscription.apparatuses.find(apparatus => apparatus.id === app.apparatusId)
      )
    },
    calcProductsOnPremiumSubscription() {
      this.apparatusesOnExistingSubscription = this.apparatuses
        ?.filter(x => this.isNonDealerPremiumProduct(x))
        .map(apparatus => {
          return {
            apparatusId: apparatus.apparatusId,
            apparatusName: apparatus.name,
            apparatusType: apparatus.type,
            country: apparatus.country
          }
        })
    },
    calcSubscriptionsPendingCancel() {
      if (
        this.subscriptionCancelInitiated === true &&
        this.subscriptions?.filter(
          subscription =>
            subscription.subscriptionStatus !== Constants.SubscriptionDetailPendingCancel &&
            subscription.store !== Constants.DealerSubscriptionStore
        ).length > 0
      ) {
        this.showPremiumFeaturesGettingReady = true
      } else {
        this.$store.dispatch('setSubscriptionPendingCancel', false)
      }
    },
    compileLegacySubscriptionList() {
      this.legacySubscriptionList = this.subscriptionDetails?.legacyBillingAccount?.products
        .filter(product =>
          this.apparatuses.some(
            apparatus =>
              apparatus?.apparatusId === product?.apparatusId &&
              (product?.status === Constants.SubscriptionActive ||
                product?.status === Constants.SubscriptionPendingCancellation ||
                product?.status === Constants.SubscriptionPendingTermination ||
                product?.status === Constants.SubscriptionPendingSuspension ||
                product?.status === Constants.SubscriptionDunning)
          )
        )
        .map(product => {
          const apparatus = this.apparatuses?.find(apparatus => apparatus?.apparatusId === product?.apparatusId)
          if (product != null && product != undefined) {
            return {
              apparatusId: apparatus.apparatusId,
              apparatusName: apparatus.name,
              apparatusType: apparatus.type,
              isDealerManaged: apparatus.isDealerManaged,
              dealerEmail: apparatus.preferredDealerEmail,
              subscriptionProductId: product?.productId,
              subscriptionProductName: product?.productName || '',
              subscriptionCost: product?.cost || 0,
              subscriptionDuration: product?.durationInMonths || 0,
              subscriptionNextBillingDate: product?.nextBillingDate || '',
              subscriptionStatus: product?.status
            }
          }
        })
    },
    compileSubscriptionsList() {
      this.subscriptions = !this.subscriptionDetails //might be null first time thru
        ? []
        : this.subscriptionDetails?.subscriptions
            ?.filter(
              subscription =>
                subscription?.apparatuses?.length > 0 && subscription?.status !== Constants.SubscriptionDetailCanceled
            )
            .map(subscription => {
              var apparatusList = []
              subscription?.apparatuses?.forEach(subApparatus => {
                const apparatus = this.apparatuses?.find(apparatus => apparatus.apparatusId == subApparatus.apparatusId)
                apparatusList.push({
                  id: apparatus?.apparatusId,
                  name: apparatus?.name,
                  type: apparatus?.type
                })
              })
              return {
                apparatuses: apparatusList,
                subscriptionCost: subscription?.product.cost || 0,
                subscriptionPendingCost: subscription?.pendingProduct?.cost || 0,
                subscriptionDurationInMonths: subscription?.product.durationInMonths || 0,
                subscriptionPendingDurationInMonths: subscription?.pendingProduct?.durationInMonths || 0,
                subscriptionProductId: subscription?.product.productId,
                subscriptionPendingProductId: subscription?.pendingProduct?.productId,
                subscriptionProductName: subscription?.product.name || '',
                subscriptionPendingProductName: subscription?.pendingProduct?.name || '',
                subscriptionRenewalDate: subscription?.renewalDate || '',
                subscriptionStore: subscription?.store,
                subscriptionStatus: subscription?.status,
                pendingProductId: subscription?.pendingProduct?.productId,
                pendingProductName: subscription?.pendingProduct?.name,
                pendingProductDescription: subscription?.pendingProduct?.description
              }
            })
    },
    compileDealerManagedSubscriptionList() {
      this.productsDealerManaged = this.apparatuses
        .filter(apparatus => apparatus.isDealerManaged)
        .map(apparatus => {
          return {
            apparatusId: apparatus.apparatusId,
            apparatusName: apparatus.name,
            apparatusType: apparatus.type
          }
        })
    }
  },
  watch: {
    apparatusesFromState() {
      this.filterApparatusesFromState()
    },
    subscriptionDetails() {
      this.compileSubscriptionsList()
      this.compileLegacySubscriptionList()
    },
    async subscriptions() {
      await this.calcShowPremiumFeaturesGettingReady()
      this.getSubForPremiumPlanCard()
      this.apparatusWithMultipleSubscriptions()
      this.numberOfApparatusInNeedOfAttention = this.getNumberOfApparatusInNeedOfAttention()
      this.premiumApparatusesNeedsAttention = this.getPremiumApparatusesNeedsAttention()
    },
    async apparatuses() {
      this.compileSubscriptionsList()
      this.compileLegacySubscriptionList()
      await this.calcShowPremiumFeaturesGettingReady()
      this.calcSubscriptionsPendingCancel()
      this.calcProductsOnPremiumSubscription()
      this.apparatusWithMultipleSubscriptions()
      this.compileDealerManagedSubscriptionList()
      this.calcProductsOnBasicSubscription()
      this.numberOfApparatusInNeedOfAttention = this.getNumberOfApparatusInNeedOfAttention()
      this.premiumApparatusesNeedsAttention = this.getPremiumApparatusesNeedsAttention()
    }
  },
  async mounted() {
    if (this.apparatusesFromState == null || this.apparatusesFromState?.length == 0) {
      await this.$store.dispatch('getApparatuses')
    }

    this.filterApparatusesFromState()

    if (this.accountProfile) {
      await this.getPaymentMethod()

      await this.$store
        .dispatch('getSubscriptionDetails', this.accountProfile.organizationId)
        .then(response => {
          this.subscriptionDetails = response.data
          const purchaseDate = this.subscriptionDetails?.subscriptions[0]?.purchasedDate
          const duration = this.subscriptionDetails?.subscriptions[0]?.product?.durationInMonths
          if (purchaseDate && duration) {
            this.endDate = this.addMonths(purchaseDate, duration)
          } else this.endDate == null
        })
        .catch(() => {})

      this.v1FuelMonitors = this.apparatuses.filter(
        apparatus => this.isApparatusV1FuelTank(apparatus) && !apparatus.isDealerManaged
      )

      this.compileLegacySubscriptionList()
      this.compileSubscriptionsList()
      this.compileDealerManagedSubscriptionList()
      this.getSubForPremiumPlanCard()
      this.calcProductsOnBasicSubscription()
      this.calcProductsOnPremiumSubscription()
      this.apparatusWithMultipleSubscriptions()
      this.numberOfApparatusInNeedOfAttention = this.getNumberOfApparatusInNeedOfAttention()
      this.premiumApparatusesNeedsAttention = this.getPremiumApparatusesNeedsAttention()
      await this.calcShowPremiumFeaturesGettingReady()
      this.calcSubscriptionsPendingCancel()
      this.isLoading = false
    } else {
      this.$store.dispatch('getAccountProfile')
    }
  }
}
</script>
<style scoped lang="scss">
.icon-and-text {
  flex-direction: row;
  display: flex;
  gap: 0.25em;
  align-items: center;
}

.collapsed .when-open,
.not-collapsed .when-closed {
  display: none;
}

.check-icon {
  color: var(--blue-700);
}

.receives-features-text {
  color: var(--carbon-600);
  font-size: 0.875em;
  line-height: 1.35;
}

.subs-info-card-body {
  padding: 0px !important;
}
.card-title {
  margin-bottom: 0;
}
.sub-radio-dot {
  font-size: 0.875rem !important;
}

.current-plan-badge {
  border: 2px solid;
  padding: 5px;
}

.pending-change-badge {
  padding: 5px;
}

#premium-enrollment-modal-step-1 {
  @media (min-width: 576px) {
    .modal-dialog {
      max-width: 200px !important;
    }
  }
  @media (min-width: 800px) {
    .modal-dialog {
      max-width: 400px !important;
    }
  }
}

.subscriptionCardText {
  font-size: 16px !important;
}

.features {
  margin-top: 0.5em;
  gap: 0.75em;
  display: flex;
  flex-direction: column;
}

.supporting-text {
  font-size: 0.875em;
  color: var(--gray-600);
  line-height: 1.25;
}

.legal-disclaimer {
  font-size: 0.875em;
  color: var(--carbon-600);
}

.bullet-points {
  li {
    margin-bottom: 0.35em;
  }
}

.shouldnt-take-long {
  text-align: center;
}

.verify-reading {
  font-style: italic;
  text-align: center;
}

.check-cellular {
  text-align: center;
}

.modal-template-header {
  padding: 160px 160px;
}

.bold-text {
  font-weight: bold;
}

li {
  margin: 0px 0;
}

.tu-help-alert {
  background-color: var(--yellow-200);
  color: var(--carbon-900);
  border: solid 1px var(--yellow-400);
}

.invalid-feedback {
  order: 2;
}
</style>
<style scoped lang="scss">
h1 {
  font-weight: normal;
  line-height: 1.15;
}

.paid-subs-info-card {
  border: 2px solid var(--carbon-300) !important;
  &:hover {
    border: 2px solid var(--brand-400) !important;
    background-color: transparent;
  }
  padding: 0 30px;
  border-radius: 8px;
}

.selected-sub-info-card {
  border: 2px solid var(--brand-400) !important;
  border-radius: 8px;
}

.card-title {
  margin-bottom: 0;
}

.selected {
  border: 1px solid var(--brand-400);
  background: white;
  box-shadow: 0px 0px 10px var(--brand-400);
}

.btn-subscription-radio {
  color: var(--carbon-900);
  font-size: 30px;
  font-weight: bold;
  padding: 0.375rem 2rem;
  border-color: var(--brand-600);
  border-width: 2px;
  margin-bottom: 10px;
}
.btn-subscription-radio:not(:disabled):not(.disabled):hover {
  background-color: var(--orange-200);
}
.btn-subscription-radio:not(:disabled):not(.disabled):focus:active {
  box-shadow: 0px 0px 8px rgba(255, 107, 0, 0.25) !important;
  border-width: 2px;
}
.btn-subscription-radio.active {
  box-shadow: 0 0 0 0.2rem rgba(255, 107, 0, 0.25) !important;
}

.sub-free-radio-text {
  font-size: 16px !important;
}

.feature {
  display: flex;
  align-items: center;
  gap: 0.5em;

  i {
    font-size: 1.125em;
    color: var(--carbon-600);
  }
  div {
    flex: 1;
  }
}
.basic-features {
  font-size: 0.875em;
  line-height: 1.35;
  margin-bottom: 0;
}

.premium-feature-text {
  font-weight: 600;
  font-size: 0.875em;
  line-height: 1.35;
  margin-bottom: 2px;
}
.premium-feature-supporting-text {
  font-size: 0.875em;
  line-height: 1.35;
  margin-bottom: 0;
  color: var(--carbon-600);
}
</style>
